import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/BlogNavigation';
import Footer from '../../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

export default function GrokTutorial() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('what-is-grok');
  const [copied, setCopied] = useState(false);
  const [linkedinMsgCopied, setLinkedinMsgCopied] = useState(false);

  // SEO + Schema.org
  useEffect(() => {
    document.title = 'How to Get a Free Grok API Key — xAI Tutorial | JobOS';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Step-by-step guide to getting a free Grok API key from xAI. Access Grok\'s powerful reasoning models with $25 in free credits. No credit card required to start.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'How to Get a Free Grok API Key — xAI Tutorial | JobOS', true);
    setMeta('og:description', 'Complete step-by-step guide to accessing xAI\'s Grok API for AI-powered job applications. $25 in free credits included.', true);
    setMeta('og:url', `${SITE_URL}/blog/grok-free-api-key`, true);
    setMeta('og:type', 'article', true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/0845358849afe66cb76edd66ff2b4c90.jpg$25%20free%20credits%2C%20no%20text%2C%20high%20quality%20digital%20illustration%2C%20deep%20black%20background%20with%20silver%20and%20white%20glow%20effects&width=1200&height=630&seq=grok-xai-og-v1&orientation=landscape', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:image:alt', 'How to Get a Free Grok API Key from xAI — $25 free credits, powerful reasoning models | JobOS', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'How to Get a Free Grok API Key — xAI Tutorial | JobOS');
    setMeta('twitter:description', 'Complete step-by-step guide to accessing xAI\'s Grok API for AI-powered job applications. $25 in free credits included.');
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Abstract%20digital%20illustration%20of%20xAI%20Grok%20artificial%20intelligence%20concept%20with%20glowing%20white%20and%20silver%20neural%20pathways%20forming%20an%20X%20shape%20in%20dark%20space%2C%20sleek%20monochrome%20tech%20aesthetic%20with%20bright%20white%20accent%20lights%20radiating%20outward%2C%20futuristic%20minimal%20art%20representing%20xAI%20machine%20intelligence%20and%20$25%20free%20credits%2C%20no%20text%2C%20high%20quality%20digital%20illustration%2C%20deep%20black%20background%20with%20silver%20and%20white%20glow%20effects&width=1200&height=630&seq=grok-xai-og-v1&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/grok-free-api-key`);

    document.querySelectorAll('script[data-schema-page="grok-tutorial"]').forEach(el => el.remove());

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'How to Get a Free Grok API Key from xAI',
        description: 'Complete step-by-step guide to accessing xAI\'s Grok API. $25 in free credits, powerful reasoning models, beginner friendly.',
        url: `${SITE_URL}/blog/grok-free-api-key`,
        datePublished: '2026-01-01',
        dateModified: '2026-03-01',
        author: { '@type': 'Organization', name: 'JobOS', url: SITE_URL },
        publisher: {
          '@type': 'Organization',
          name: 'JobOS',
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/og-image.png` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/grok-free-api-key` },
        keywords: 'Grok API key, xAI API, free Grok API, Grok tutorial, xAI credits, Grok AI model',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Get a Free Grok API Key from xAI',
        description: 'Get a free Grok API key from xAI with $25 in free credits.',
        totalTime: 'PT5M',
        step: [
          { '@type': 'HowToStep', position: 1, name: 'Sign up for an xAI account', text: 'Go to console.x.ai and create a free account.', url: 'https://console.x.ai' },
          { '@type': 'HowToStep', position: 2, name: 'Navigate to API settings', text: 'Go to the API Keys section in your xAI console dashboard.' },
          { '@type': 'HowToStep', position: 3, name: 'Generate your API key', text: 'Click "Create API Key", name it, and copy it immediately.' },
          { '@type': 'HowToStep', position: 4, name: 'Start building with Grok', text: 'Paste your key into JobOS Settings → AI Provider → Grok.' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: 'Grok Free API Key Tutorial', item: `${SITE_URL}/blog/grok-free-api-key` },
        ],
      },
    ];

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'grok-tutorial');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-page="grok-tutorial"]').forEach(el => el.remove());
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(progress);

      const sections = ['what-is-grok', 'prerequisites', 'step-by-step', 'rate-limits', 'troubleshooting', 'security'];
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element && element.getBoundingClientRect().top <= 150) {
          setActiveSection(section);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-100 z-50">
        <div 
          className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[500px] overflow-hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=A%20stunning%20deep%20space%20visualization%20featuring%20an%20intricate%20neural%20network%20constellation%20with%20glowing%20purple%20and%20blue%20nodes%20connected%20by%20luminous%20pathways%20against%20a%20dark%20cosmic%20background%20with%20distant%20stars%20and%20nebulae%20creating%20an%20atmosphere%20of%20advanced%20artificial%20intelligence%20and%20cosmic%20computing%20power%20representing%20xAI%20Grok%20technology%20with%20ethereal%20light%20rays%20and%20digital%20particles%20floating%20in%20the%20vastness%20of%20space&width=1920&height=500&seq=grok-hero-space-neural&orientation=landscape"
          alt="Grok AI Tutorial Hero"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-6 max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-purple-600/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-purple-400/30">
              <i className="ri-sparkling-2-fill text-purple-300"></i>
              <span className="text-sm font-medium text-purple-200">Tutorial Series</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              How to Get a Free Grok API Key
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Complete step-by-step guide to accessing xAI's Grok API for your AI-powered applications
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <span>Updated January 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>8 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-bar-chart-line"></i>
                <span>Beginner Friendly</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Published / Last Updated bar */}
      <div className="w-full border-b border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <i className="ri-quill-pen-line text-purple-500"></i>
            <span className="font-medium text-gray-700">Published:</span>
            <time dateTime="2026-01-01" className="font-mono">January 1, 2026</time>
          </div>
          <div className="w-px h-4 bg-gray-200 hidden sm:block"></div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-600 text-xs font-bold">
              <i className="ri-refresh-line text-xs"></i>
              Last updated: March 2026
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs font-bold">
              <i className="ri-shield-check-line text-xs"></i>
              Credits &amp; limits verified
            </span>
          </div>
          <div className="ml-auto text-xs text-gray-400 font-mono hidden md:block">
            Data sourced from console.x.ai
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex gap-12">
          {/* Sidebar */}
          <aside className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* TL;DR Box */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-600 rounded-lg">
                    <i className="ri-lightbulb-flash-line text-white text-lg"></i>
                  </div>
                  <h3 className="font-bold text-gray-900">TL;DR</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-0.5 flex-shrink-0"></i>
                    <span>Sign up for xAI account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-0.5 flex-shrink-0"></i>
                    <span>Navigate to API settings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-0.5 flex-shrink-0"></i>
                    <span>Generate your API key</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-0.5 flex-shrink-0"></i>
                    <span>Start building with Grok</span>
                  </li>
                </ul>
              </div>

              {/* Table of Contents */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-list-unordered text-purple-600"></i>
                  Table of Contents
                </h3>
                <nav className="space-y-2">
                  {[
                    { id: 'what-is-grok', label: 'What is Grok?' },
                    { id: 'prerequisites', label: 'Prerequisites' },
                    { id: 'step-by-step', label: 'Step-by-Step Guide' },
                    { id: 'rate-limits', label: 'Rate Limits & Pricing' },
                    { id: 'troubleshooting', label: 'Troubleshooting' },
                    { id: 'security', label: 'Security Best Practices' }
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${
                        activeSection === item.id
                          ? 'bg-purple-50 text-purple-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tutorial Series */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-book-open-line text-purple-600"></i>
                  Tutorial Series
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/blog/google-ai-studio-free-api-key"
                    className="block px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-all whitespace-nowrap"
                  >
                    Google AI Studio API
                  </Link>
                  <div className="px-3 py-2 rounded-lg text-sm bg-purple-50 text-purple-700 font-medium whitespace-nowrap">
                    Grok API (Current)
                  </div>
                  <Link
                    to="/blog/groq-free-api-key"
                    className="block px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-all whitespace-nowrap"
                  >
                    Groq API
                  </Link>
                  <Link
                    to="/blog/openrouter-free-api-key"
                    className="block px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-all whitespace-nowrap"
                  >
                    OpenRouter API
                  </Link>
                </div>
              </div>
            </div>
          </aside>

          {/* Article Content */}
          <article className="flex-1 max-w-3xl">
            <div className="prose prose-lg max-w-none">
              {/* What is Grok */}
              <section id="what-is-grok" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg">
                    <i className="ri-sparkling-2-fill text-white"></i>
                  </div>
                  What is Grok?
                </h2>
                <div className="bg-gray-50 rounded-xl p-8 mb-6 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    [Content to be added: Overview of xAI's Grok, its capabilities, unique features, and use cases]
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    [Content to be added: Key advantages and what makes Grok different from other AI models]
                  </p>
                </div>
              </section>

              {/* Prerequisites */}
              <section id="prerequisites" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg">
                    <i className="ri-checkbox-circle-line text-white"></i>
                  </div>
                  Prerequisites
                </h2>
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-gray-700">
                      <i className="ri-arrow-right-circle-fill text-purple-600 mt-1 flex-shrink-0"></i>
                      <span>[Content to be added: Required accounts and setup]</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-700">
                      <i className="ri-arrow-right-circle-fill text-purple-600 mt-1 flex-shrink-0"></i>
                      <span>[Content to be added: Technical requirements]</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-700">
                      <i className="ri-arrow-right-circle-fill text-purple-600 mt-1 flex-shrink-0"></i>
                      <span>[Content to be added: Verification steps]</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Step-by-Step Guide */}
              <section id="step-by-step" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg">
                    <i className="ri-footprint-line text-white"></i>
                  </div>
                  Step-by-Step Guide
                </h2>
                <div className="space-y-8">
                  {[1, 2, 3, 4, 5].map((step) => (
                    <div key={step} className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-purple-200 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl text-white font-bold text-lg flex-shrink-0">
                          {step}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">
                            [Step {step} Title]
                          </h3>
                          <p className="text-gray-700 leading-relaxed mb-4">
                            [Content to be added: Detailed instructions for step {step}]
                          </p>
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <p className="text-sm text-gray-600">
                              [Screenshot or code example placeholder]
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Rate Limits */}
              <section id="rate-limits" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg">
                    <i className="ri-speed-line text-white"></i>
                  </div>
                  Rate Limits & Pricing
                </h2>
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    [Content to be added: Free tier limits, pricing structure, and usage guidelines]
                  </p>
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">
                      [Table or list of rate limits and pricing tiers]
                    </p>
                  </div>
                </div>
              </section>

              {/* Troubleshooting */}
              <section id="troubleshooting" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg">
                    <i className="ri-tools-line text-white"></i>
                  </div>
                  Troubleshooting
                </h2>
                <div className="space-y-4">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="bg-white rounded-xl p-6 border border-gray-200">
                      <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <i className="ri-error-warning-line text-purple-600"></i>
                        [Common Issue {item}]
                      </h3>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        [Content to be added: Solution and explanation]
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Security Best Practices */}
              <section id="security" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg">
                    <i className="ri-shield-check-line text-white"></i>
                  </div>
                  Security Best Practices
                </h2>
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8 border border-red-200">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-xl flex-shrink-0">
                      <i className="ri-alarm-warning-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Important Security Notice</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        [Content to be added: Critical security warnings and best practices]
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-gray-700 text-sm">
                      <i className="ri-shield-check-fill text-red-600 mt-0.5 flex-shrink-0"></i>
                      <span>[Security tip 1]</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-700 text-sm">
                      <i className="ri-shield-check-fill text-red-600 mt-0.5 flex-shrink-0"></i>
                      <span>[Security tip 2]</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-700 text-sm">
                      <i className="ri-shield-check-fill text-red-600 mt-0.5 flex-shrink-0"></i>
                      <span>[Security tip 3]</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* CTA Box */}
              <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-10 text-center text-white mb-16">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-2xl mx-auto mb-6">
                  <i className="ri-rocket-2-line text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold mb-4">Ready to Automate Your Job Applications?</h3>
                <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto leading-relaxed">
                  Now that you have your Grok API key, use JobOS to automatically fill job applications with AI-powered precision.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <span>Try JobOS Free</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              {/* Share Section */}
              <div className="flex flex-wrap items-center gap-4 mb-10 pt-8 border-t border-gray-200">
                <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">Share:</span>
                <a
                  href="https://twitter.com/intent/tweet?text=How%20to%20Get%20a%20Free%20Grok%20API%20Key%20%E2%80%94%20Full%20Tutorial&url=https://jobos.dev/blog/grok-free-api-key"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-white hover:bg-black transition-all cursor-pointer"
                  title="Share on X (Twitter)"
                >
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <div className="flex items-center gap-1.5">
                  <a
                    href="https://www.linkedin.com/sharing/share-offsite/?url=https://jobos.dev/blog/grok-free-api-key"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-white hover:bg-[#0077B5] transition-all cursor-pointer"
                    title="Share on LinkedIn"
                  >
                    <i className="ri-linkedin-fill text-lg"></i>
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('How to Get a Free Grok API Key from xAI — complete step-by-step guide with $25 in free credits. Full tutorial: https://jobos.dev/blog/grok-free-api-key');
                      setLinkedinMsgCopied(true);
                      setTimeout(() => setLinkedinMsgCopied(false), 2500);
                    }}
                    className={`flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${linkedinMsgCopied ? 'bg-[#0077B5] border-[#0077B5] text-white' : 'bg-gray-100 border-gray-200 text-gray-500 hover:border-[#0077B5] hover:text-[#0077B5]'}`}
                    title="Copy message to paste into LinkedIn"
                  >
                    <i className={linkedinMsgCopied ? 'ri-check-line' : 'ri-clipboard-line'}></i>
                    {linkedinMsgCopied ? 'Copied!' : 'Copy message'}
                  </button>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText('https://jobos.dev/blog/grok-free-api-key');
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all cursor-pointer ${copied ? 'bg-purple-600 border-purple-600 text-white' : 'bg-gray-100 border-gray-200 text-gray-500 hover:bg-purple-600 hover:border-purple-600 hover:text-white'}`}
                  title={copied ? 'Copied!' : 'Copy link'}
                >
                  <i className={copied ? 'ri-check-line text-lg' : 'ri-link text-lg'}></i>
                </button>
              </div>

              {/* Continue Learning */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-book-open-line text-purple-600 text-2xl"></i>
                  Continue Learning
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <Link
                    to="/blog/google-ai-studio-free-api-key"
                    className="bg-white rounded-xl p-6 border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all group"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4 group-hover:bg-blue-200 transition-all">
                      <i className="ri-google-fill text-blue-600 text-xl"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 whitespace-nowrap">Google AI Studio</h4>
                    <p className="text-sm text-gray-600">Get your free API key</p>
                  </Link>
                  <Link
                    to="/blog/groq-free-api-key"
                    className="bg-white rounded-xl p-6 border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all group"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg mb-4 group-hover:bg-orange-200 transition-all">
                      <i className="ri-flashlight-fill text-orange-600 text-xl"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 whitespace-nowrap">Groq API</h4>
                    <p className="text-sm text-gray-600">Lightning-fast inference</p>
                  </Link>
                  <Link
                    to="/blog/openrouter-free-api-key"
                    className="bg-white rounded-xl p-6 border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all group"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg mb-4 group-hover:bg-amber-200 transition-all">
                      <i className="ri-route-line text-amber-600 text-xl"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 whitespace-nowrap">OpenRouter</h4>
                    <p className="text-sm text-gray-600">Access multiple models</p>
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}